import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Notification from '../components/Notification';
import ManageCategory from '../components/ManageCategory';
import {
  FileText, Shield, Home, Globe, Phone, Flower, Heart,
  Users, Star, ClipboardList,
  CheckCircle, AlertTriangle,
  IndianRupee
} from 'lucide-react';
import Button from '../components/elements/Button';
import Layout from '../components/Layout';
import { getMe } from '../utils/authApi';
import { fetchRecordCategory } from '../utils/dataApi';

const Dashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [pageReady, setPageReady] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const isSubscribed = sessionStorage.getItem('isSubscribed') === 'true';
  const role = sessionStorage.getItem('role');
  const isLoggedIn = sessionStorage.getItem('isLoggedIn') === 'true';
  const [notification, setNotification] = useState({ message: '', type: 'success' });
  const [license, setLicense] = useState(sessionStorage.getItem('license') ? JSON.parse(sessionStorage.getItem('license')) : null);

  useEffect(() => {
    if (!isSubscribed) {
      navigate('/subscription-check');
      return;
    }
    
    document.title = "Dashboard | Eternal Locker™";
    if (!isLoggedIn) {
      navigate('/');
      return;
    } else {
      // Check if profile is complete
      getMe().then((res) => {
        if (res.success && res.user) {
          const { firstName, lastName, contact, dob, address } = res.user;
          const isProfileComplete = firstName && lastName && contact && dob && address;
          
          if (!isProfileComplete) {
            navigate('/profile');
            return;
          }
        }
        setPageReady(true);
      }).catch(() => {
        setPageReady(true);
      });
    }
  }, []);

  useEffect(() => {
    // `?category=` is still honoured: it is how the dashboard's own in-app navigation
    // arrives here, and older alert emails already delivered may still carry it.
    const categoryFromUrl = searchParams.get('category');
    if (categoryFromUrl) {
      setSelectedCategory(visibleCategories.find(cat => cat.dbCategory === categoryFromUrl) || null);
      return;
    }

    // `?id=` alone is what alert-reminder emails now send, so that the category is not
    // written into a URL that mail gateways log and forwarded messages carry. Ask the
    // server which category the record is in instead of being told by the link.
    const recordIdFromUrl = searchParams.get('id');
    if (!recordIdFromUrl) return;

    let cancelled = false;
    (async () => {
      const response = await fetchRecordCategory(recordIdFromUrl);
      // Silent on failure by design. A record that was deleted between the reminder
      // being sent and the link being clicked is an ordinary outcome, not an error worth
      // an alarming message -- the dashboard simply opens on its category list.
      if (cancelled || !response?.success || !response.record?.category) return;
      setSelectedCategory(visibleCategories.find(cat => cat.dbCategory === response.record.category) || null);
    })();

    return () => { cancelled = true; };
  }, [searchParams]);

  const handleManageClick = async (category) => {
    setSelectedCategory(category);
  };

  // Mapping the database ENUMs to friendly titles and icons
  const dataCategories = [
    {
      dbCategory: 'LEGAL_DOCUMENTS',
      title: "Legal Documents",
      icon: <FileText />,
      description: "Will, Power of Attorney, Trust documentation, and Living Will."
    },
    {
      dbCategory: 'PERSONAL_IDENTIFICATION',
      title: "Personal Identification",
      icon: <Shield />,
      description: "Passport, Aadhar/SSN, Birth/Marriage Certificates, and IDs."
    },
    {
      dbCategory: 'FINANCIAL_INFORMATION',
      title: "Financial Information",
      icon: <IndianRupee />,
      description: "Bank, investment, and retirement accounts, insurance policies, and debts."
    },
    {
      dbCategory: 'PROPERTY_AND_ASSETS',
      title: "Property & Assets",
      icon: <Home />,
      description: "Deeds, vehicle titles, valuables, and safe deposit box details."
    },
    {
      dbCategory: 'DIGITAL_PRESENCE',
      title: "Digital Presence",
      icon: <Globe />,
      description: "Email, social media, online banking, and subscription service logins."
    },
    {
      dbCategory: 'KEY_CONTACTS',
      title: "Key Contacts",
      icon: <Phone />,
      description: "Details for family, friends, lawyer, financial advisor, and accountant."
    },
    {
      dbCategory: 'FUNERAL_AND_BURIAL_WISHES',
      title: "Funeral & Burial Wishes",
      icon: <Flower />, // Using Flower as an alternative icon for burial preferences
      description: "Service type, preferred funeral home, specific ceremony wishes, and pre-plans."
    },
    {
      dbCategory: 'HEALTHCARE_INFORMATION',
      title: "Healthcare Information",
      icon: <Heart />,
      description: "Medical history, current medications, and doctor/healthcare provider contacts."
    },
    {
      dbCategory: 'MISCELLANEOUS_ITEMS',
      title: "Miscellaneous Items",
      icon: <Star />,
      description: "Pet care instructions, memberships, and personal wishes or messages."
    },
  ];

  // Nominees are no longer one of these. They were the tenth tile here, edited through
  // the same record modal as a will or a bank account, which was always a category error:
  // a vault record is the OWNER's own information, while a nominee is a third party's
  // contact details held under a declaration the owner signed. Migration 009 gave them
  // their own table and /manage-nominees gives them their own page - reached from the card
  // below rather than from this grid.
  //
  // This also retires the filter that used to hide the tile from a nominee. There is
  // nothing left here to hide: the roster is not a category, so a nominee browsing an
  // owner's locker simply cannot arrive at it, by the ?category= deep link or otherwise.
  const visibleCategories = dataCategories;


  if (!pageReady) {
    return null;
  }

  return (
    <Layout>
      <Notification message={notification.message} onClose={() => setNotification({ message: '', type: 'success' })} type={notification.type} />
      <div className="flex sm:flex-row flex-col justify-between items-center border-b border-accent-main pb-4">
        <h1 className="sm:text-3xl text-2xl font-extrabold text-accent-main flex items-center">
          <ClipboardList className="mr-3 w-7 h-7" />
          All Your Digital Assets
        </h1>

        <div className="ml-auto flex items-center gap-2">
          {isSubscribed ? (
            <span
              className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold flex items-center hover:bg-green-200 transition"
            >
              <CheckCircle className="w-4 h-4 mr-1" /> Active Subscription - {license?.name}
            </span>
          ) : (
            <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-bold flex items-center">
              <AlertTriangle className="w-4 h-4 mr-1" /> No Subscription
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-4 gap-3">
        <p className="text-lg text-primary-dark m-0">
          Click a category below to securely add and manage your critical information.
        </p>
      </div>

      {/* Nominees are deliberately NOT one of the tiles below. They are not the owner's
          own records - they are the people those records are for - so they get their own
          entry point, and their own page. Owners only: a nominee reading this locker has
          no business with the roster of who else was designated. */}
      {role !== 'nominee' && (
        <div className="mb-6 p-6 bg-primary-light border border-accent-main rounded-xl flex flex-col sm:flex-row sm:items-center gap-4 shadow-lg">
          <Users className="w-8 h-8 text-accent-main flex-shrink-0" />
          <div className="flex-1">
            <h3 className="text-xl font-bold text-primary-dark">Your Nominees</h3>
            <p className="text-primary-dark text-sm">
              The people who will be given access to this locker if the inactivity check
              completes. Without at least one, nobody can be granted access.
            </p>
          </div>
          <Button size="small" onClick={() => navigate('/manage-nominees')}>Manage Nominees</Button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {visibleCategories.map((cat, index) => (
          <div key={index} className="p-6 bg-primary-light border border-accent-main rounded-xl space-y-3 shadow-lg hover:shadow-2xl hover:scale-105 transition duration-200">
            <div className="flex items-start justify-between">
              {React.cloneElement(cat.icon, { className: 'w-8 h-8 mr-3 text-accent-main flex-shrink-0' })}
              
              {/* {selectedCategory?.title === cat.title ? (
                  <Loading className={'text-primary-dark'}/>
              ) : ( */}
              <Button
                size='small'
                onClick={() => handleManageClick(cat)}
                loading={selectedCategory && selectedCategory?.title === cat.title}
                disabled={selectedCategory && selectedCategory?.title !== cat.title}
              >
                  Manage
              </Button>
              {/* )} */}
            </div>
            <h3 className="text-xl font-bold text-primary-dark pt-2">{cat.title}</h3>
            <p className="text-primary-dark text-sm">{cat.description}</p>
          </div>
        ))}
      </div>

      {/* Manage Modal */}
      <ManageCategory selectedCategory={selectedCategory} onClose={() => setSelectedCategory(null)} />

      {/* The Success Message Box (to replace alert()) */}
      <div id="confirmation-message" className="fixed bottom-4 right-4 p-4 bg-accent-main text-white rounded-lg shadow-xl hidden transition-all duration-300">
        <CheckCircle className="w-5 h-5 inline mr-2" />
        <span></span>
      </div>
    </Layout>
  );
};

export default Dashboard;
